import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { request } from '../api/client'
import type { HuntFilters } from '../api/queries'

interface Draft {
  rule_id: string
  title: string
  sigma: string
  level: string
  resolved: { sigma: string; ocsf: string; value: string }[]
  warnings: string[]
  attack_techniques: string[]
}

interface Saved extends Draft {
  detail: string
}

const CATEGORIES: { value: string; label: string }[] = [
  { value: '', label: 'any Linux event' },
  { value: 'authentication', label: 'authentication' },
  { value: 'process_creation', label: 'process creation' },
  { value: 'network_connection', label: 'network connection' },
]

const LEVELS = ['informational', 'low', 'medium', 'high', 'critical']

/**
 * Turning the hunt on screen into a rule.
 *
 * An analyst who has just found something by hand has done the hard part; the gap
 * between that and a detection is transcription, and transcription is where the
 * mistake gets made. So the dialog does the translation and then shows its work: the
 * generated Sigma, and — the part that matters — the OCSF field every condition
 * actually resolved to. `User` means the process owner in a generic Linux rule and
 * the authenticating account in an authentication one, and a rule that picked the
 * wrong one would compile, enable, and match nothing forever.
 *
 * The preview is not optional and cannot be skipped, and saving produces a draft that
 * says, in the API's own words, that it is not detecting yet.
 */
export function SaveAsRule({
  filters,
  canSave,
  onClose,
}: {
  filters: HuntFilters
  canSave: boolean
  onClose: () => void
}) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [level, setLevel] = useState('medium')
  const [category, setCategory] = useState('')
  const [techniques, setTechniques] = useState('')
  const dialog = useRef<HTMLDivElement>(null)

  useEffect(() => {
    dialog.current?.querySelector('input')?.focus()
  }, [])

  const payload = {
    title: title || 'Untitled',
    description,
    level,
    category,
    techniques: techniques
      .split(',')
      .map((technique) => technique.trim())
      .filter(Boolean),
    filters: {
      q: filters.q ?? '',
      hostname: filters.hostname ?? '',
      user: filters.user ?? '',
      src_ip: filters.src_ip ?? '',
    },
  }

  const preview = useQuery({
    queryKey: ['rule-preview', payload],
    queryFn: () =>
      request<Draft>('/rules/drafts/preview/', { method: 'POST', body: payload }),
    retry: false,
  })

  const save = useMutation({
    mutationFn: () => request<Saved>('/rules/drafts/', { method: 'POST', body: payload }),
  })

  return (
    <div
      ref={dialog}
      className="panel"
      role="dialog"
      aria-modal="false"
      aria-labelledby="save-as-rule"
      style={{ marginTop: 'var(--space-4)' }}
    >
      <h3 id="save-as-rule" style={{ marginTop: 0 }}>
        Save this hunt as a rule
      </h3>

      {save.isSuccess ? (
        <>
          <p role="status">{save.data.detail}</p>
          <p className="mono muted">{save.data.rule_id}</p>
          <button onClick={onClose}>Close</button>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <label>
              <span className="muted">Title</span>
              <input
                aria-label="Rule title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>
            <label>
              <span className="muted">Severity</span>
              <select
                aria-label="Severity"
                value={level}
                onChange={(event) => setLevel(event.target.value)}
              >
                {LEVELS.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {/* Not cosmetic: the category decides what the field names mean. */}
              <span className="muted">These events are</span>
              <select
                aria-label="Log source category"
                value={category}
                onChange={(event) => setCategory(event.target.value)}
              >
                {CATEGORIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="muted">ATT&amp;CK techniques</span>
              <input
                aria-label="ATT&CK techniques"
                placeholder="T1078.003, …"
                value={techniques}
                onChange={(event) => setTechniques(event.target.value)}
              />
            </label>
          </div>
          <label style={{ marginTop: 'var(--space-2)' }}>
            <span className="muted">Why this matters, for whoever gets the alert at 03:00</span>
            <textarea
              aria-label="Description"
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>

          {preview.isError && (
            <p className="error" role="alert">
              {(preview.error as Error).message}
            </p>
          )}

          {preview.data && (
            <>
              {preview.data.warnings.map((warning) => (
                <p className="error" role="alert" key={warning}>
                  {warning}
                </p>
              ))}

              <h4 style={{ marginBottom: 'var(--space-1)' }}>Where each field lands</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <caption className="muted" style={{ captionSide: 'bottom', textAlign: 'left' }}>
                  The rule searches the right-hand column. If that is not the field you were
                  looking at, the rule will not find what you found.
                </caption>
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th scope="col">Rule field</th>
                    <th scope="col">Value</th>
                    <th scope="col">Searches</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.data.resolved.map((row) => (
                    <tr key={row.sigma}>
                      <td className="mono">{row.sigma}</td>
                      <td className="mono">{row.value}</td>
                      <td className="mono">{row.ocsf}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <details style={{ marginTop: 'var(--space-3)' }}>
                <summary>The rule itself</summary>
                <pre className="mono" style={{ overflow: 'auto' }}>
                  {preview.data.sigma}
                </pre>
              </details>
            </>
          )}

          {save.isError && (
            <p className="error" role="alert">
              {(save.error as Error).message}
            </p>
          )}

          <p className="muted" style={{ marginBottom: 'var(--space-2)' }}>
            Saving creates a draft. It detects nothing until it has been backtested over a real
            window and enabled.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              className="primary"
              disabled={!canSave || !preview.data || !title.trim() || save.isPending}
              onClick={() => save.mutate()}
            >
              Save as a draft
            </button>
            <button onClick={onClose}>Cancel</button>
          </div>
          {!canSave && (
            <p className="muted" style={{ marginBottom: 0 }}>
              Your role can hunt but not change what the platform detects, so this draft cannot
              be saved from here.
            </p>
          )}
        </>
      )}
    </div>
  )
}
